import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight, BookOpen, Clock, Info, Sparkles, Target, AlertTriangle, Globe, Layers, PenLine, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const MODULE_LABEL = {
    gottman: "Gottman Method",
    act: "ACT",
    dbt: "DBT",
    cbt: "CBT",
    eft: "EFT",
    mi: "Motivational Interviewing",
};

export default function LearnChapterPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [chapter, setChapter] = useState(null);
    const [notFound, setNotFound] = useState(false);
    useDocumentTitle(chapter ? chapter.title : "Learn");

    useEffect(() => {
        let cancelled = false;
        setChapter(null);
        setNotFound(false);
        api.get(`/learn/chapters/${slug}`)
            .then((r) => { if (!cancelled) setChapter(r.data); })
            .catch((e) => {
                if (cancelled) return;
                if (e?.response?.status === 404) setNotFound(true);
                else toast.error("Could not load chapter");
            });
        return () => { cancelled = true; };
    }, [slug]);

    if (notFound) {
        return (
            <Layout>
                <div className="mx-auto max-w-2xl py-16 text-center" data-testid="learn-chapter-not-found">
                    <h1 className="text-2xl font-semibold text-foreground">Chapter not found</h1>
                    <p className="mt-2 text-sm text-muted-foreground">It may have moved or been removed.</p>
                    <Button asChild variant="secondary" className="mt-6">
                        <Link to="/learn">Back to Learn</Link>
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <article className="mx-auto max-w-[720px] space-y-8 pb-32 animate-fade-in-up" data-testid="learn-chapter-page">
                {/* Top bar */}
                <div>
                    <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground" data-testid="learn-chapter-back">
                        <Link to={chapter?.module_slug ? `/learn/${chapter.module_slug}` : "/learn"}>
                            <ArrowLeft className="h-4 w-4" />
                            {chapter?.module_slug ? `Back to ${MODULE_LABEL[chapter.module_slug] || "module"}` : "All methods"}
                        </Link>
                    </Button>
                </div>

                {/* Header */}
                {!chapter ? (
                    <div className="space-y-3">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-10 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                    </div>
                ) : (
                    <header className="space-y-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-primary" data-testid="learn-chapter-order">
                            Chapter {chapter.order} · {MODULE_LABEL[chapter.module_slug] || "Method"}
                        </p>
                        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="learn-chapter-title">
                            {chapter.title}
                        </h1>
                        <p className="text-base text-muted-foreground" data-testid="learn-chapter-subtitle">
                            {chapter.subtitle}
                        </p>
                        <p className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            ≈ {chapter.estimated_minutes} min read
                            <span className="text-border">·</span>
                            <BookOpen className="h-3.5 w-3.5" />
                            5-question quiz at the end
                        </p>
                    </header>
                )}

                {/* Glossary chips */}
                {chapter && chapter.key_terms?.length > 0 && (
                    <section data-testid="learn-key-terms">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Key terms
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {chapter.key_terms.map((kt) => (
                                <Popover key={kt.term}>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            data-testid={`learn-key-term-${kt.term.replace(/\s+/g, "-").toLowerCase()}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/10"
                                        >
                                            <Info className="h-3 w-3" />
                                            {kt.term}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72 text-sm" align="start">
                                        <p className="font-semibold text-foreground">{kt.term}</p>
                                        <p className="mt-1 text-muted-foreground">{kt.definition}</p>
                                    </PopoverContent>
                                </Popover>
                            ))}
                        </div>
                    </section>
                )}

                {/* Sections */}
                {!chapter ? (
                    <div className="space-y-6">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-40 w-full rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <div className="space-y-10" data-testid="learn-chapter-sections">
                        {chapter.sections.map((s, i) => (
                            <section key={i} data-testid={`learn-section-${i}`}>
                                <h2 className="mb-3 text-xl font-semibold tracking-tight text-foreground">
                                    {s.heading}
                                </h2>
                                <MarkdownBody markdown={s.body} />
                            </section>
                        ))}
                    </div>
                )}

                {/* In practice — Phase 17 case examples */}
                {chapter && chapter.case_examples?.length > 0 && (
                    <CaseExamplesSection examples={chapter.case_examples} />
                )}

                {/* Phase 18 — Try it yourself */}
                {chapter && chapter.exercises?.length > 0 && (
                    <ExercisesSection exercises={chapter.exercises} />
                )}

                {/* Phase 18 — Skills you'll build */}
                {chapter && chapter.skills_checklist?.length > 0 && (
                    <SkillsChecklistSection skills={chapter.skills_checklist} />
                )}

                {/* Phase 18 — Common pitfalls */}
                {chapter && chapter.common_pitfalls?.length > 0 && (
                    <PitfallsSection pitfalls={chapter.common_pitfalls} />
                )}

                {/* Phase 18 — Cultural considerations */}
                {chapter && chapter.cultural_considerations && (
                    <CulturalConsiderationsSection text={chapter.cultural_considerations} />
                )}

                {/* Phase 18 — Blending with other approaches */}
                {chapter && chapter.combining_with_other_methods && (
                    <CombiningSection text={chapter.combining_with_other_methods} />
                )}

                {/* Phase 18 — Further reading */}
                {chapter && chapter.further_reading?.length > 0 && (
                    <FurtherReadingSection references={chapter.further_reading} />
                )}

                {/* Phase 18 — For your own reflection */}
                {chapter && chapter.reflection_prompts?.length > 0 && (
                    <ReflectionPromptsSection prompts={chapter.reflection_prompts} />
                )}

                {/* Floating CTA */}
                {chapter && (
                    <div
                        className="fixed inset-x-0 bottom-0 z-20 border-t border-border/70 bg-background/95 px-4 py-3 backdrop-blur"
                        data-testid="learn-chapter-quiz-cta-bar"
                    >
                        <div className="mx-auto flex max-w-[720px] items-center justify-between gap-3">
                            <p className="text-xs text-muted-foreground sm:text-sm">
                                Done reading? Test what stuck.
                            </p>
                            <Button
                                onClick={() => navigate(`/learn/chapter/${chapter.slug}/quiz`)}
                                className="bg-primary hover:bg-primary-hover"
                                data-testid="learn-chapter-take-quiz-button"
                            >
                                Take the 5-question quiz
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </article>
        </Layout>
    );
}

/**
 * Long-form reading style. Georgia for prose paragraphs, generous line-height,
 * scoped markdown styling so we don't pollute Tailwind's preflight.
 */
const MarkdownBody = ({ markdown }) => (
    <div
        className={cn(
            "font-serif text-[1.0625rem] leading-[1.75] text-foreground",
            "[&_p]:mb-4 [&_p:last-child]:mb-0",
            "[&_strong]:font-semibold [&_strong]:text-foreground",
            "[&_em]:italic",
            "[&_ul]:my-3 [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul>li]:list-disc [&_ul>li]:marker:text-muted-foreground",
            "[&_ol]:my-3 [&_ol]:space-y-2 [&_ol]:pl-5 [&_ol>li]:list-decimal [&_ol>li]:marker:text-muted-foreground",
            "[&_li>p]:mb-0",
            "[&_code]:font-mono [&_code]:text-sm [&_code]:bg-secondary [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded"
        )}
    >
        <ReactMarkdown>{markdown}</ReactMarkdown>
    </div>
);

/**
 * Phase 17 — "In practice" case examples.
 * Renders 2 vignettes per chapter: scenario, application, dialogue bubbles, outcome.
 */
const CaseExamplesSection = ({ examples }) => (
    <section data-testid="learn-case-examples" className="space-y-6 border-t border-border/60 pt-10">
        <header className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                In practice
            </h2>
        </header>
        <p className="text-sm text-muted-foreground">
            Two short vignettes showing how this technique plays out in a real session.
        </p>
        <div className="space-y-6">
            {examples.map((ex, idx) => (
                <Card
                    key={idx}
                    data-testid={`learn-case-example-${idx}`}
                    className="overflow-hidden border-border/70 bg-card"
                >
                    <CardContent className="space-y-5 p-6">
                        <header>
                            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                                Vignette {idx + 1}
                            </p>
                            <h3 className="mt-1 text-lg font-semibold tracking-tight text-foreground">
                                {ex.title}
                            </h3>
                        </header>

                        <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Scenario
                            </p>
                            <p className="text-[15px] leading-relaxed text-foreground/90">
                                {ex.scenario}
                            </p>
                        </div>

                        <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Application
                            </p>
                            <p className="text-[15px] leading-relaxed text-foreground/90">
                                {ex.application}
                            </p>
                        </div>

                        <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Dialogue
                            </p>
                            <ul className="space-y-2">
                                {ex.dialogue.map((turn, ti) => (
                                    <DialogueBubble key={ti} speaker={turn.speaker} text={turn.text} />
                                ))}
                            </ul>
                        </div>

                        <div className="rounded-xl border border-accent/40 bg-accent/10 p-4">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                                Outcome
                            </p>
                            <p className="text-[15px] leading-relaxed text-foreground/90">
                                {ex.outcome}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    </section>
);

const DialogueBubble = ({ speaker, text }) => {
    const isCounsellor = /^counsellor$/i.test(speaker?.trim() || "");
    return (
        <li
            data-testid="learn-dialogue-turn"
            className={cn(
                "flex gap-3",
                isCounsellor ? "flex-row" : "flex-row-reverse"
            )}
        >
            <div
                className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed",
                    isCounsellor
                        ? "bg-secondary text-foreground rounded-tl-sm"
                        : "bg-primary/10 text-foreground rounded-tr-sm"
                )}
            >
                <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {speaker}
                </p>
                <p>{text}</p>
            </div>
        </li>
    );
};


/* =========================================================================
 * Phase 18 — Deep content sections. Each renders only when data is present.
 * ========================================================================= */

const SectionHeader = ({ icon: Icon, label }) => (
    <header className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="text-xl font-semibold tracking-tight text-foreground">{label}</h2>
    </header>
);

const ExercisesSection = ({ exercises }) => (
    <section data-testid="learn-exercises" className="space-y-5 border-t border-border/60 pt-10">
        <SectionHeader icon={Target} label="Try it yourself" />
        <p className="text-sm text-muted-foreground">
            Three short exercises to put this chapter into practice.
        </p>
        <div className="space-y-4">
            {exercises.map((ex, i) => (
                <Card
                    key={i}
                    data-testid={`learn-exercise-${i}`}
                    className="border-l-4 border-l-primary border-border/70"
                >
                    <CardContent className="space-y-3 p-5">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <h3 className="text-base font-semibold tracking-tight text-foreground">
                                {ex.title}
                            </h3>
                            {ex.time_required && (
                                <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground/80">
                                    {ex.time_required}
                                </span>
                            )}
                        </div>
                        <p className="text-[15px] leading-relaxed text-foreground/90">
                            {ex.instructions}
                        </p>
                        {ex.what_youll_learn && (
                            <p className="text-sm italic text-muted-foreground">
                                What you&apos;ll learn: {ex.what_youll_learn}
                            </p>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    </section>
);

const SkillsChecklistSection = ({ skills }) => (
    <section data-testid="learn-skills-checklist" className="space-y-4 border-t border-border/60 pt-10">
        <SectionHeader icon={CheckCircle2} label="Skills you'll build" />
        <p className="text-sm text-muted-foreground">
            Concrete competencies a counsellor should have after this chapter.
        </p>
        <ul className="space-y-2.5">
            {skills.map((skill, i) => (
                <li
                    key={i}
                    data-testid={`learn-skill-${i}`}
                    className="flex gap-3 rounded-xl border border-border/60 bg-card px-4 py-3"
                >
                    <div
                        className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded border-2 border-primary/40 bg-background"
                        aria-hidden="true"
                    />
                    <p className="text-[15px] leading-relaxed text-foreground/90">{skill}</p>
                </li>
            ))}
        </ul>
    </section>
);

const PitfallsSection = ({ pitfalls }) => (
    <section data-testid="learn-pitfalls" className="space-y-5 border-t border-border/60 pt-10">
        <SectionHeader icon={AlertTriangle} label="Common pitfalls" />
        <p className="text-sm text-muted-foreground">
            Mistakes counsellors-in-training reliably make. Naming them in advance is half the fix.
        </p>
        <div className="space-y-4">
            {pitfalls.map((p, i) => (
                <Card
                    key={i}
                    data-testid={`learn-pitfall-${i}`}
                    className="border-amber-300/60 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/5"
                >
                    <CardContent className="space-y-2.5 p-5">
                        <div className="flex gap-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600 dark:text-amber-400" />
                            <p className="font-semibold text-foreground">{p.pitfall}</p>
                        </div>
                        {p.why_it_happens && (
                            <p className="text-[14px] leading-relaxed text-muted-foreground">
                                <span className="font-medium text-foreground/70">Why it happens: </span>
                                {p.why_it_happens}
                            </p>
                        )}
                        <p className="text-[14px] leading-relaxed text-foreground/90">
                            <span className="font-medium text-foreground/70">What to do instead: </span>
                            {p.what_to_do_instead}
                        </p>
                    </CardContent>
                </Card>
            ))}
        </div>
    </section>
);

const CulturalConsiderationsSection = ({ text }) => (
    <section data-testid="learn-cultural" className="space-y-4 border-t border-border/60 pt-10">
        <SectionHeader icon={Globe} label="Cultural considerations" />
        <Card className="border-border/60 bg-secondary/40">
            <CardContent className="p-5">
                <p className="text-[15px] leading-relaxed text-foreground/90">{text}</p>
            </CardContent>
        </Card>
    </section>
);

const MODULE_BY_KEYWORD = [
    { match: /gottman/i, slug: "gottman" },
    { match: /\bACT\b|psychological flexibility|hexaflex/i, slug: "act" },
    { match: /\bDBT\b|Linehan|dialectical/i, slug: "dbt" },
    { match: /\bCBT\b|cognitive[-\s]behaviou?ral/i, slug: "cbt" },
    { match: /\bEFT\b|emotionally[-\s]focused/i, slug: "eft" },
    { match: /\bMI\b|motivational interviewing|OARS/i, slug: "mi" },
];

const linkifyMethodMentions = (text) => {
    // Split text on method-mentioning tokens; render those as Link chips.
    const parts = [];
    let remaining = text;
    let key = 0;
    const tokenRegex = /(\*\*[^*]+\*\*|Gottman|ACT|DBT|CBT|EFT|MI)/g;
    let lastIndex = 0;
    let match;
    while ((match = tokenRegex.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
            parts.push(remaining.slice(lastIndex, match.index));
        }
        const token = match[0];
        const inner = token.startsWith("**") ? token.slice(2, -2) : token;
        const matched = MODULE_BY_KEYWORD.find((m) => m.match.test(inner));
        if (matched) {
            parts.push(
                <Link
                    key={`m-${key++}`}
                    to={`/learn/${matched.slug}`}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[13px] font-medium text-primary hover:bg-primary/20"
                >
                    {inner}
                </Link>
            );
        } else {
            parts.push(inner);
        }
        lastIndex = match.index + token.length;
    }
    if (lastIndex < remaining.length) parts.push(remaining.slice(lastIndex));
    return parts;
};

const CombiningSection = ({ text }) => (
    <section data-testid="learn-combining" className="space-y-4 border-t border-border/60 pt-10">
        <SectionHeader icon={Layers} label="Blending with other approaches" />
        <Card className="border-accent/40 bg-accent/10">
            <CardContent className="p-5">
                <p className="text-[15px] leading-relaxed text-foreground/90">
                    {linkifyMethodMentions(text)}
                </p>
            </CardContent>
        </Card>
    </section>
);

const FurtherReadingSection = ({ references }) => (
    <section data-testid="learn-further-reading" className="space-y-4 border-t border-border/60 pt-10">
        <SectionHeader icon={BookOpen} label="Further reading" />
        <ul className="space-y-3">
            {references.map((r, i) => (
                <li
                    key={i}
                    data-testid={`learn-reference-${i}`}
                    className="flex gap-3 rounded-xl border border-border/60 bg-card p-4"
                >
                    <BookOpen className="mt-1 h-4 w-4 flex-none text-muted-foreground" />
                    <div className="space-y-0.5">
                        <p className="text-[15px] font-semibold text-foreground">
                            {r.title}
                            {r.kind && (
                                <span className="ml-2 align-middle rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-foreground/70">
                                    {r.kind}
                                </span>
                            )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            {r.authors} · {r.year}
                        </p>
                        {(r.note || r.why_this_matters) && (
                            <p className="text-sm italic text-foreground/80">{r.note || r.why_this_matters}</p>
                        )}
                    </div>
                </li>
            ))}
        </ul>
    </section>
);

const ReflectionPromptsSection = ({ prompts }) => (
    <section data-testid="learn-reflection" className="space-y-4 border-t border-border/60 pt-10">
        <SectionHeader icon={PenLine} label="For your own reflection" />
        <p className="text-sm text-muted-foreground">
            Open-ended questions to journal with — no right answers.
        </p>
        <div className="space-y-3">
            {prompts.map((q, i) => (
                <div
                    key={i}
                    data-testid={`learn-reflection-${i}`}
                    className="rounded-xl border border-border/60 bg-secondary/40 p-4"
                >
                    <p className="text-[15px] leading-relaxed text-foreground/90">{q}</p>
                </div>
            ))}
        </div>
    </section>
);
